/**
 * How a bulk-change row updates the chosen column.
 */
export type BulkChangeOperation = 'replace' | 'prefix' | 'suffix';

/**
 * Which value control a bulk-change column uses.
 */
export type BulkChangeValueKind =
  | 'text'
  | 'number'
  | 'supplier'
  | 'department'
  | 'subDepartment'
  | 'lineColour'
  | 'tags'
  | 'onHold';

/**
 * Column the bulk-change modal can edit.
 */
export interface IBulkChangeColumn {
  field: string;
  kind: BulkChangeValueKind;
  label: string;
  operations: BulkChangeOperation[];
  /** Step for number inputs. */
  numberStep?: string;
}

/**
 * One confirmed column change, ready to apply to every matched item.
 */
export interface IBulkChangeRow {
  field: string;
  operation: BulkChangeOperation;
  summary: string;
  value: string | number | string[];
  /** When Current GP % is set, snap each item's calculated retail price to the store price bands. */
  usePriceBanding?: boolean;
}

/**
 * Result returned when the column editor modal is accepted.
 */
export interface IBulkChangeEditorResult {
  rows: IBulkChangeRow[];
}

/**
 * Result returned when the confirmation modal is accepted.
 */
export interface IBulkChangeConfirmResult {
  confirmed: boolean;
}

/**
 * A matched item that a Current GP % change will leave unchanged.
 */
export interface IBulkChangeExcludedItem {
  code: string;
  desc: string;
  latestCost: number;
  reason: string;
}

/**
 * In-progress row inside the column editor modal.
 */
export interface IBulkChangeDraftRow {
  allowBlank: boolean;
  field: string;
  id: string;
  multiValues: string[];
  numberValue: string;
  operation: BulkChangeOperation;
  singleValue: string;
  textValue: string;
  usePriceBanding: boolean;
}

/** Bulk changes are refused when the matched set reaches this size. */
export const BULK_CHANGE_MAX_ITEMS = 1000;

/** How many stock codes are sent in one stock update, matching a grid submit of that size. */
export const BULK_CHANGE_WRITE_BATCH_SIZE = 500;

/** Sentinel option key for clearing the supplier. */
export const BULK_CHANGE_BLANK_SUPPLIER_KEY = '__blank__';

/** Retail price and current GP are written together. */
export const BULK_CHANGE_LINKED_PRICE_FIELDS: readonly string[] = ['sellPriIncl1', 'nominalGP'];

/** IQ item category for non-stock items (stock field 5). */
export const BULK_CHANGE_NON_STOCK_CATEGORY = 2;

/** Shown when Current GP % is the column being changed. */
export const CURRENT_GP_BULK_CHANGE_EXCLUSION_NOTE =
  'Items with a latest cost of R0, and non-stock items (category 2), are excluded from this change.';

/**
 * Columns Stock Manager can already edit, excluding locked fields and multi-value editors
 * that do not map to replace / prefix / suffix.
 */
export const BULK_CHANGE_COLUMNS: readonly IBulkChangeColumn[] = [
  {
    field: 'desc',
    label: 'Description',
    kind: 'text',
    operations: ['replace', 'prefix', 'suffix'],
  },
  { field: 'barcode', label: 'Barcode', kind: 'text', operations: ['replace', 'prefix', 'suffix'] },
  {
    field: 'binL',
    label: 'Bin Location',
    kind: 'text',
    operations: ['replace', 'prefix', 'suffix'],
  },
  {
    field: 'genCode',
    label: 'General Code',
    kind: 'text',
    operations: ['replace', 'prefix', 'suffix'],
  },
  {
    field: 'suppCode',
    label: 'Supplier Code',
    kind: 'text',
    operations: ['replace', 'prefix', 'suffix'],
  },
  { field: 'regularSuppCode', label: 'Supplier', kind: 'supplier', operations: ['replace'] },
  { field: 'dep', label: 'Department', kind: 'department', operations: ['replace'] },
  { field: 'subDep', label: 'Sub Department', kind: 'subDepartment', operations: ['replace'] },
  { field: 'lineColourCode', label: 'Line Colour', kind: 'lineColour', operations: ['replace'] },
  {
    field: 'ordLvl',
    label: 'Order Level',
    kind: 'number',
    operations: ['replace'],
    numberStep: '1',
  },
  {
    field: 'maxOrderLevel',
    label: 'Max Order Level',
    kind: 'number',
    operations: ['replace'],
    numberStep: '1',
  },
  {
    field: 'minOrderLevel',
    label: 'Min Order Level',
    kind: 'number',
    operations: ['replace'],
    numberStep: '1',
  },
  {
    field: 'orderQty',
    label: 'Order Quantity',
    kind: 'number',
    operations: ['replace'],
    numberStep: '1',
  },
  {
    field: 'packSize',
    label: 'Pack Size',
    kind: 'number',
    operations: ['replace'],
    numberStep: '1',
  },
  {
    field: 'sellPriIncl1',
    label: 'Retail Price',
    kind: 'number',
    operations: ['replace'],
    numberStep: '0.01',
  },
  {
    field: 'nominalGP',
    label: 'Current GP %',
    kind: 'number',
    operations: ['replace'],
    numberStep: '0.01',
  },
  {
    field: 'recommendedGP',
    label: 'Target GP %',
    kind: 'number',
    operations: ['replace'],
    numberStep: '0.01',
  },
  { field: '_tags', label: 'Tags', kind: 'tags', operations: ['replace'] },
  { field: 'onHoldCode', label: 'On Hold Code', kind: 'onHold', operations: ['replace'] },
];

/**
 * Creates an empty editor row.
 */
export function createBulkChangeDraftRow(): IBulkChangeDraftRow {
  return {
    allowBlank: false,
    field: '',
    id: crypto.randomUUID(),
    multiValues: [],
    numberValue: '',
    operation: 'replace',
    singleValue: '',
    textValue: '',
    usePriceBanding: false,
  };
}
